import { Box, Skeleton } from '@mui/material';
import { ReactComponent as PauseIcon } from 'assets/svg/pause-asset.svg';
import { ReactComponent as PlayIcon } from 'assets/svg/play-asset.svg';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ConditionalLayer } from 'ui-blocks/ReusableComponents';
import { baseVideoStyles } from './BaseVideoStyles';

function BaseVideo(
	{
		src,
		type = 'video/mp4',
		controls = true,
		autoPlay = false,
		muted = false,
		loop = false,
		style,
		captions = null,
		playPauseIcon = true,
		playsInline = false,
		isTesting = false,
		showSkeletonLoading = false,
		onError = () => {
			// Error handling
		},
		containerStyle = {},
		...props
	},
	ref,
) {
	const [isVideoLoaded, setIsVideoLoaded] = useState(false);
	const videoRef = useRef(null);

	const onVideoLoadedData = () => {
		if (videoRef.current) {
			if (videoRef.current.readyState >= 2) {
				setIsVideoLoaded(true);
			} else {
				setIsVideoLoaded(false);
			}
		}
	};

	useEffect(() => {
		onVideoLoadedData();
	}, [src]);

	const [isPlaying, setIsPlaying] = useState(false);

	useImperativeHandle(
		ref,
		() => ({
			handleVideoPlay: () => {
				if (isVideoLoaded) {
					videoRef.current.play().catch(() => setIsPlaying(false));
					setIsPlaying(true);
				}
			},
			handleVideoPause: () => {
				if (isVideoLoaded) {
					videoRef.current.pause();
					setIsPlaying(false);
				}
			},
		}),
		[isVideoLoaded],
	);

	return (
		<Box sx={{ position: 'relative', ...containerStyle }}>
			{!isVideoLoaded && showSkeletonLoading && (
				<Skeleton
					variant="rectangular"
					width="100%"
					height="100%"
					animation="wave"
					sx={[{ position: 'absolute', top: 0, left: 0 }, style]}
				/>
			)}
			<video
				ref={videoRef}
				data-testid="video"
				style={{ ...style }}
				onLoadedData={onVideoLoadedData}
				controls={controls}
				autoPlay={autoPlay}
				muted={muted}
				loop={loop}
				onError={onError}
				playsInline={playsInline}
				onEnded={() => setIsPlaying(false)}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...props}
			>
				<source src={src} type={type} />
				<track kind="captions" src={captions} srcLang="en" label="English" default />
				Your browser does not support the video tag.
			</video>
			<Box
				id="video-assets-button-container"
				data-testid="video-assets-button-container"
				sx={baseVideoStyles.videoAssetButtonContainer}
			>
				<ConditionalLayer
					data-testid="video-loaded-conditional-layer"
					isShown={(isVideoLoaded || isTesting) && playPauseIcon}
				>
					<Box
						id="video-assets-hover-buttons"
						data-testid="icon-container"
						sx={baseVideoStyles.videoAssetHoverButtons}
						onClick={(e) => {
							e.stopPropagation();
							try {
								if (videoRef.current?.play) {
									if (videoRef.current.paused) {
										videoRef.current.play().catch(() => {
											// Error handling
										});
									} else {
										videoRef.current.pause();
									}
									setIsPlaying((prev) => !prev);
								}
							} catch (err) {
								// Error handling
							}
						}}
					>
						<ConditionalLayer isShown={!isPlaying}>
							<PlayIcon data-testid="play-icon" />
						</ConditionalLayer>
						<ConditionalLayer isShown={isPlaying}>
							<PauseIcon data-testid="pause-icon" />
						</ConditionalLayer>
					</Box>
				</ConditionalLayer>
			</Box>
		</Box>
	);
}

export default React.forwardRef(BaseVideo);
